import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Printer } from "lucide-react";
import { api, imgUrl, formatError, DAYS } from "@/lib/api";

const WALK_IN_ROWS = 5;
const DAY_FALLBACK = { "2026-10-30": "Friday, Oct 30", "2026-10-31": "Saturday, Oct 31", "2026-11-01": "Sunday, Nov 1", "2026-11-02": "Monday, Nov 2" };

const PRINT_CSS = `
@page { size: letter portrait; margin: 0.5in; }
.sheet { font-family: "Manrope", Arial, sans-serif; color: #000; }
.sheet table { width: 100%; border-collapse: collapse; font-size: 11pt; }
.sheet th, .sheet td { border: 1px solid #000; padding: 5px 6px; text-align: left; vertical-align: middle; }
.sheet th { background: #eee; font-size: 9pt; text-transform: uppercase; letter-spacing: .04em; }
.sheet thead { display: table-header-group; }
.sheet tfoot { display: table-row-group; }
.sheet tr, .sheet tbody.keep { break-inside: avoid; page-break-inside: avoid; }
.sheet .box { display: inline-block; width: 14px; height: 14px; border: 1.5px solid #000; }
.sheet .day-page { break-after: page; page-break-after: always; }
.sheet .day-page:last-child { break-after: auto; page-break-after: auto; }
@media print {
  html, body { background: #fff !important; }
  .no-print { display: none !important; }
  .sheet { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .sheet .day-page { padding: 0 !important; margin: 0 !important; box-shadow: none !important; }
}
`;

const DayTable = ({ day, label, households, logo, printedAt }) => {
  const people = households.reduce((n, r) => n + r.party_size, 0);
  return (
    <section className="day-page mx-auto mb-8 max-w-[8.5in] bg-white p-8 shadow-md" data-testid={`print-day-${day}`}>
      <header className="mb-4 flex items-center gap-4 border-b-2 border-black pb-3">
        {logo && <img src={imgUrl(logo.url)} alt="Ilesa Ire" className="h-14 w-auto" />}
        <div className="flex-1">
          <h1 className="text-xl font-bold leading-tight">Egúngún Festival 2026 – Gate Check-in</h1>
          <p className="text-base font-semibold" data-testid={`print-day-label-${day}`}>{label}</p>
        </div>
        <p className="text-right text-xs">Printed {printedAt}</p>
      </header>
      <table>
        <thead>
          <tr>
            <th style={{ width: 34 }}>✓</th>
            <th>Household</th>
            <th style={{ width: 120 }}>Phone</th>
            <th style={{ width: 52 }}>Party</th>
            <th style={{ width: 52 }}>Vol.</th>
            <th style={{ width: 62 }}>Giveaway</th>
            <th style={{ width: 78 }}>Arrived #</th>
          </tr>
        </thead>
        <tbody>
          {households.map((r) => (
            <tr key={r.id} data-testid={`print-row-${day}-${r.id}`}>
              <td><span className="box" /></td>
              <td className="font-semibold">{r.name}</td>
              <td>{r.phone}</td>
              <td className="text-center">{r.party_size}</td>
              <td className="text-center">{r.volunteer ? "Y" : ""}</td>
              <td className="text-center">{r.giveaway ? "Y" : ""}</td>
              <td />
            </tr>
          ))}
          {households.length === 0 && (
            <tr><td colSpan={7} className="text-center italic">No households registered for this day.</td></tr>
          )}
        </tbody>
        <tbody className="keep">
          <tr data-testid={`print-totals-${day}`}>
            <td colSpan={3} className="font-bold">Expected: {households.length} household{households.length === 1 ? "" : "s"}</td>
            <td className="text-center font-bold">{people}</td>
            <td colSpan={2} className="text-xs">people expected</td>
            <td />
          </tr>
          <tr><td colSpan={7} className="bg-neutral-100 text-xs font-bold uppercase tracking-wider">Walk-ins</td></tr>
          {Array.from({ length: WALK_IN_ROWS }).map((_, i) => (
            <tr key={`w${i}`} className="h-8" data-testid={`print-walkin-${day}-${i}`}>
              <td><span className="box" /></td><td /><td /><td /><td /><td /><td />
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
};

export default function PrintSheet() {
  const [params] = useSearchParams();
  const day = params.get("day") || "all";
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [printedAt] = useState(() => new Date().toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }));

  useEffect(() => {
    document.title = "Gate Check-in Sheet · Egúngún Festival 2026";
    const prev = document.body.style.background;
    document.body.style.background = "#fff";
    Promise.all([api.get("/admin/registrations"), api.get("/content")])
      .then(([regs, content]) => setData({ regs: regs.data.items, content: content.data }))
      .catch((e) => setError(formatError(e)));
    return () => { document.body.style.background = prev; };
  }, []);

  useEffect(() => {
    if (!data || params.get("autoprint") === "0") return;
    const id = setTimeout(() => window.print(), 600);
    return () => clearTimeout(id);
  }, [data, params]);

  if (error) return <p className="p-8 text-black">{error}</p>;
  if (!data) return <p className="p-8 text-black" data-testid="print-loading">Preparing sheet…</p>;

  const days = day === "all" ? DAYS : DAYS.filter((d) => d === day);
  const schedule = Object.fromEntries((data.content.schedule || []).map((s) => [s.day, s]));
  const labelFor = (d) => {
    const s = schedule[d];
    return s ? `${DAY_FALLBACK[d]} — ${s.title}` : DAY_FALLBACK[d];
  };
  const byName = (a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" });

  return (
    <div className="sheet min-h-screen bg-neutral-200 py-6 print:bg-white print:py-0" data-testid="print-sheet">
      <style>{PRINT_CSS}</style>
      <div className="no-print mx-auto mb-6 flex max-w-[8.5in] items-center justify-between gap-3 px-2">
        <p className="text-sm text-neutral-700">{days.length > 1 ? "All days — one page per day" : labelFor(days[0])}</p>
        <button onClick={() => window.print()} className="flex items-center gap-2 rounded-full bg-black px-5 py-2.5 text-sm font-semibold text-white hover:bg-neutral-800" data-testid="print-sheet-button">
          <Printer className="h-4 w-4" /> Print
        </button>
      </div>
      {days.map((d) => (
        <DayTable key={d} day={d} label={labelFor(d)} logo={data.content.images?.logo} printedAt={printedAt}
          households={data.regs.filter((r) => r.days.includes(d)).sort(byName)} />
      ))}
    </div>
  );
}
